import { useForm } from 'react-hook-form'
import {
  forwardRef,
  type ForwardedRef,
  useCallback,
  useImperativeHandle,
} from 'react'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon-v2'

import KinerjaDropdownGroup from 'app/KinerjaPage/components/KinerjaDropdown/KinerjaDropdownGroup'
import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import StickyFooter from 'app/KinerjaPage/components/StickyFooter'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'

import type {
  KinerjaPerencanaanTabBaseProps,
  KinerjaSelection,
  SKPTabHandler,
  UpdateSKPPayload,
} from 'types/kinerja'

import ImgBadgeStarRibbonPro from 'assets/icons/profil-kompetensi/img-badge-star-ribbon-pro.svg'

const filterEmptyValues = (value: KinerjaSelection) => value.id !== 0

interface PerencanaanTabTugasTambahanProps
  extends KinerjaPerencanaanTabBaseProps {
  customStickyFooter?: () => JSX.Element
  editReviewData?: boolean
  adjustmentInfoText?: string
  showCheckButton?: boolean
  onChange?: (values: KinerjaSelection[]) => void
}

const TabTugasTambahan = (
  {
    skp,
    isSubmitting = false,
    onClickNext = () => {},
    onClickSave = () => {},
    editReviewData = false,
    onChange = () => {},
    adjustmentInfoText,
    showCheckButton,
    customStickyFooter,
  }: PerencanaanTabTugasTambahanProps,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const { trackEvent } = useSKPTrackEvent()
  const { contentTugasTambahan } = useContentContext()

  const initValues =
    (editReviewData ? skp?.reviewTugasTambahan : skp?.tugasTambahan) ?? []
  const { setValue, getValues, handleSubmit } = useForm<{
    tugasTambahan: KinerjaSelection[]
  }>({
    defaultValues: {
      tugasTambahan: initValues,
    },
  })

  const handleChange = (values: KinerjaSelection[]) => {
    setValue('tugasTambahan', values.filter(filterEmptyValues))
    onChange(values.filter(filterEmptyValues))
  }

  const generatePayload = useCallback((): UpdateSKPPayload => {
    const { tugasTambahan } = getValues()

    return {
      id: skp.id,
      tugasTambahan: !tugasTambahan.length
        ? ([{}] as KinerjaSelection[]) // Required default value for BE validation
        : tugasTambahan.filter(filterEmptyValues),
    }
  }, [getValues, skp])

  const onSubmit = () => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PLANNING_TO_PERILAKU_KERJA',
      screen_name: 'KINERJA_PLANNING_TUGAS_TAMBAHAN_PAGE',
      kinerja_planning_id: skp?.id,
      kinerja_planning_status: skp?.status,
    })
    onClickNext(generatePayload())
  }

  const onSaveDraft = useCallback(() => {
    trackEvent({
      event_name: 'CLICK_KINERJA_DRAFT_PLANNING',
      screen_name: 'KINERJA_PLANNING_TUGAS_TAMBAHAN_PAGE',
      kinerja_planning_id: skp?.id,
      kinerja_planning_status: skp?.status,
    })
    onClickSave(generatePayload())
  }, [trackEvent, skp, onClickSave, generatePayload])

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="p-9">
        <div className="flex gap-3">
          <ImgBadgeStarRibbonPro className="w-16 h-16 shrink-0" />
          <div>
            <Text variant="display-sm">Tugas Tambahan (Opsional)</Text>
            <Text className="mt-1.5">
              Pilih sesuai Surat Keputusan yang diberikan oleh atasan dan
              jenjang satuan pendidikan Anda. Anda juga dapat berdiskusi dengan
              atasan terkait pilihan ini.
            </Text>
          </div>
        </div>

        <div className="mt-6 rounded p-4 border border-subdued">
          <div className="flex items-center gap-1">
            <Icon>error</Icon>
            <Text variant="action">Berdasarkan </Text>
            <a
              href="https://pusatinformasi.guru.kemdikbud.go.id/hc/en-us/articles/26299452064409"
              target="_blank"
              rel="noreferrer"
            >
              <Text as="span" variant="action" color="primary">
                peraturan yang berlaku
              </Text>
              :
            </a>
          </div>
          <Text color="subdued" className="mt-1">
            Jika tugas yang diberikan kepada Anda tidak termasuk dalam Tugas
            Tambahan di sini, maka Anda dapat melewati pengisian Tugas Tambahan
            dan lanjut ke halaman berikutnya.
          </Text>
        </div>

        <div className="flex flex-col gap-9 mt-6">
          <KinerjaDropdownGroup
            content={contentTugasTambahan}
            kinerja={initValues}
            onChange={handleChange}
            allowDeleteSingleValue={true}
            defaultUnselectedTargetQty="-"
            adjustmentInfoText={adjustmentInfoText}
            showCheckButton={showCheckButton}
            {...(editReviewData
              ? {
                  originalKinerja: initValues,
                  showChanges: true,
                  kinerjaType: 'tugasTambahan',
                }
              : {})}
          />
        </div>
      </div>

      <StickyFooter>
        {customStickyFooter ? (
          customStickyFooter()
        ) : (
          <>
            <Button
              size="lg"
              color="white"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              onClick={onSaveDraft}
              data-testid="btn-save-draft"
            >
              Simpan draf
            </Button>
            <Button
              size="lg"
              color="black"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              type="submit"
              data-testid="btn-next"
            >
              Ke Perilaku Kerja
            </Button>
          </>
        )}
      </StickyFooter>
    </form>
  )
}

export default forwardRef<SKPTabHandler, PerencanaanTabTugasTambahanProps>(
  TabTugasTambahan
)
